import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDrawer } from '@angular/material/sidenav';
import { UtilService } from '@app/shared/services/utils/util.service';
import { distinctUntilChanged, filter, Subscription } from 'rxjs';
import { IDrawerConfig } from './drawer.interface';
import { DrawerService } from './drawer.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FileUploadStatusEnum } from '../file-upload/constants';
import { FileUploadService } from '@app/shared/services/utils/file-upload.service';

@Component({
  selector: 'cm-drawer',
  templateUrl: './drawer.component.html',
  styleUrls: ['./drawer.component.scss'],
})
@UntilDestroy()
export class DrawerComponent implements OnInit, OnDestroy {
  @ViewChild('cmDrawer', { static: false })
  public matDrawer: MatDrawer;

  @ViewChild('drawerContent', { static: false })
  public drawerContent: TemplateRef<any>;

  public drawerConfig: IDrawerConfig;
  public isMobile: boolean;
  public drawerOutlet: TemplateRef<any>;
  public disabled: boolean;
  private isLoading: boolean;
  private loadingProcessSubscription: Subscription;
  defaultDesktopLgWidthPercentage = '35%';

  constructor(
    private drawerService: DrawerService,
    private bottomSheet: MatBottomSheet,
    private fileUploadService: FileUploadService,
    private cdref: ChangeDetectorRef
  ) {}

  @HostListener('window:resize', [])
  public onResize(): void {
    this.handleResizeSlideOpen();
    this.isMobile = UtilService.isMobileAndTabletSize();
  }

  ngOnInit(): void {
    this.onResize();

    this.drawerService.toggle$.pipe(untilDestroyed(this)).subscribe((options) => {
      if (options?.open) {
        if (!this.isMobile) {
          this.matDrawer.open();
        } else {
          this.openBottomSheet();
        }
      } else {
        this.matDrawer.close();
        this.closeBottomSheet();
      }
    });

    this.drawerService.loading$.pipe(untilDestroyed(this)).subscribe((loading) => {
      this.isLoading = loading;
      this.cdref.detectChanges();
    });

    this.drawerService.drawerConfig$.pipe(untilDestroyed(this)).subscribe((config) => {
      this.drawerConfig = config;
      this.defaultDesktopLgWidthPercentage = '35%';
      if (config.desktopLgWidthPercentage) {
        this.defaultDesktopLgWidthPercentage = `${config.desktopLgWidthPercentage}%`;
      }
      this.cdref.detectChanges();
    });

    this.drawerService.contentChange$.pipe(untilDestroyed(this), filter(Boolean)).subscribe((template) => {
      this.drawerOutlet = template;
      this.cdref.detectChanges();
    });

    this.drawerService.toggleDisabledButton$
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe((disabled) => {
        this.disabled = disabled;
        this.cdref.detectChanges();
      });

    this.loadingProcessSubscription = this.fileUploadService.uploadStatus$.subscribe(({ file, uploadStatus }) => {
      if (file) {
        const { entity } = file.fileEntity;

        if (entity !== this.drawerService.data.entity) this.drawerConfig.primaryButton.customLoading = false;
        else {
          switch (uploadStatus) {
            case FileUploadStatusEnum.INPROCESS:
              if (this.drawerConfig.primaryButton) {
                this.drawerConfig.primaryButton.customLoading = true;
              }
              break;
            case (FileUploadStatusEnum.FAILED, FileUploadStatusEnum.FINISHED):
              if (this.drawerConfig.primaryButton) {
                this.drawerConfig.primaryButton.customLoading = false;
              }
              break;
          }
        }
      }
    });
  }

  public toggle(): void {
    this.drawerService.toggle();
  }

  public onScrollEnd(): void {
    this.drawerService.scrollEnds$.next();
  }

  private handleResizeSlideOpen(): void {
    if (this.isMobile === true && UtilService.isMobileAndTabletSize() === false) {
      // Became desktop
      if (this.bottomSheet?._openedBottomSheetRef) {
        this.closeBottomSheet();
        this.matDrawer.open();
      }
    } else if (this.isMobile === false && UtilService.isMobileAndTabletSize() === true) {
      // Became mobile
      if (this.matDrawer.opened) {
        this.matDrawer.close();
        this.openBottomSheet();
      }
    }
  }

  private closeBottomSheet(): void {
    this.bottomSheet?.dismiss();
    this.drawerService.afterClose$.next(false);
  }

  private openBottomSheet(): void {
    const bottomSheetRef = this.bottomSheet.open(this.drawerContent, {
      panelClass: 'cm-drawer-bottom-sheet',
      backdropClass: 'cm-drawer-bottom-sheet__backdrop',
      disableClose: false,
      closeOnNavigation: true,
    });

    bottomSheetRef
      .backdropClick()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.closeBottomSheet();
      });
  }

  get primaryButtonLabel() {
    if (this.drawerConfig.primaryButton?.isLoadingOverrides)
      return this.isCustomLoading
        ? this.drawerConfig.primaryButton.loadingLabel
        : this.drawerConfig.primaryButton.label;
    else return this.isLoading ? this.drawerConfig.primaryButton.loadingLabel : this.drawerConfig.primaryButton.label;
  }

  ngOnDestroy() {
    this.loadingProcessSubscription.unsubscribe();
  }

  onClose() {
    this.drawerService.close();

    // To not display empty drawer during its close transition
    setTimeout(() => (this.drawerConfig = { title: '' }), 300);
  }

  get isCustomLoading() {
    return this.drawerConfig.primaryButton.customLoading;
  }

  get isDisabled() {
    if (this.disabled) return true;

    if (this.drawerConfig.primaryButton?.isLoadingOverrides) return this.isCustomLoading;

    return this.isLoading;
  }

  onSecondaryEvent() {
    if (this.drawerConfig.secondaryButton?.overrideDisabled && this.drawerConfig.secondaryButton.customDisabled) return;
    // we are not diferentiating the disable state of secondary buttons from
    // the primary one, if we disable the primary the secondary will get disabled two or
    // not run its clickHandler here.
    // A fuction to differentiate them on demand was created bellow
    const allowClickHandler = this.drawerService.allowClickHandler(this.drawerConfig.secondaryButton?.label);
    if (!this.disabled || allowClickHandler) this.drawerConfig.secondaryButton.clickHandler();
  }
}
